import React, { useContext, useState, useEffect, useRef } from 'react';

import { ArrowRightIcon, CloseIcon } from '$partials/icon';
import { ModalContext } from '$contexts/modal.jsx';
import { Button } from '$partials/ui';
import { getLocation } from '$utilities/location';
import { useCursorContext } from '$contexts/cursor.jsx';

const useShareLinks = url => !url ? {} : ({
    twitter: `https://twitter.com/intent/tweet?url=${url}&text=`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
});

const useClipboard = () => {
    const [ isClipboardSupported, setIsClipboardSupported ] = useState(false);

    useEffect(() => {
        navigator?.permissions
            ?.query({ name: 'clipboard-write' })
            ?.then(result => {
                if (result.state === 'granted' || result.state === 'prompt') {
                    setIsClipboardSupported(true);
                }
            })
            ?.catch((error) => {
                console.error(error);
            });
    }, []);

    const write = (value, onSuccess, onFailure) => {
        if (!isClipboardSupported && !navigator?.clipboard) {
            onFailure();
        }

        navigator.clipboard.writeText(value).then(onSuccess, onFailure);
    };

    return { write, isSupported: isClipboardSupported };
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {React.FC<any>}
 */
const ShareModal = () => {
    const { handleModal } = useContext(ModalContext);

    const { dispatch: cursorDispatch } = useCursorContext();
    const exitButtonRef = useRef(null);

    const url = getLocation().href;
    const { facebook, twitter, linkedin } = useShareLinks(url);
    const { write: writeOnClipboard } = useClipboard();
    const [ copyLabel, setCopyLabel ] = useState('Copy link');

    useEffect(() => {
        const exit = cursorDispatch({
            type: 'registerArea',
            data: {
                ref: exitButtonRef,
                cursorType: 'exit'
            },
        });

        return () => [exit].map(cleanup => {
            cleanup();});
    }, []);

    return (
        <>
            <div
                className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-black mix-blend-multiply z3 cursor-none'}
                onClick={() => handleModal()}
                ref={exitButtonRef}
                style={{opacity: '0.2'}}>
            </div>
            <div
                className='height-100 bg-grey z4 fixed top-0 overflow-auto modal-container'>
                <button
                    aria-label='Close'
                    className='form-field-reset link-reset absolute right-0 right mt6 mr6 cursor-pointer'
                    onClick={() => handleModal()}
                    type='button'>
                    <CloseIcon/>
                </button>
                <div className='py7 px6'>
                    <h2 className='type-style-2'>Share</h2>
                    <ul
                        className='list-reset mt8 overflow-y-auto flex flex-wrap'>
                        <li className={'col-12 border-bottom grey'}>
                            <span className='black'>
                                <Button
                                    as = 'a'
                                    className = 'bg-white type-style-5'
                                    href={facebook}
                                    look = 'fill'
                                    renderIcon = {<ArrowRightIcon/>}>
                                    Facebook
                                </Button>
                            </span>
                        </li>
                        <li className={'col-12 border-top border-bottom grey'}>
                            <span className='black'>
                                <Button
                                    as = 'a'
                                    className = 'bg-white type-style-5'
                                    href={linkedin}
                                    look = 'fill'
                                    renderIcon = {<ArrowRightIcon/>}>
                                    LinkedIn
                                </Button>
                            </span>
                        </li>
                        <li className='col-12 border-top border-bottom grey'>
                            <span className='black'>
                                <Button
                                    as = 'a'
                                    className = 'bg-white type-style-5'
                                    href={twitter}
                                    look = 'fill'
                                    renderIcon = {<ArrowRightIcon/>}>
                                    Twitter
                                </Button>
                            </span>
                        </li>
                        <li className='col-12 border-top grey'>
                            <Button
                                as = 'button'
                                className = 'bg-white type-style-5 black'
                                look = 'fill'
                                onClick={() => (writeOnClipboard(url), setCopyLabel('Copied!'), setTimeout(() => setCopyLabel('Copy link'), 2000))}
                                renderIcon = {<ArrowRightIcon/>}>
                                {copyLabel}
                            </Button>
                        </li>
                    </ul>
                </div>
            </div>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ShareModal ;
